import type { ManagementTimelineQuery } from '@internal/spassu-code-contract';
import type { Dispatch, MutableRefObject } from 'react';
import { useCallback } from 'react';
import type { CockpitNodeKind } from '../model/cockpitViewModel';
import type {
  CockpitControllerAction,
  CockpitControllerState,
} from './cockpitControllerTypes';
import type { CockpitRequestCoordinator } from './cockpitRequestCoordinator';

type SnapshotLoader = (revision?: number, polling?: boolean) => Promise<void>;
type TimelineLoader = (
  query: ManagementTimelineQuery,
  reset: boolean,
) => Promise<void>;

export function useCockpitSnapshotActions(
  stateRef: MutableRefObject<CockpitControllerState>,
  loadSnapshot: SnapshotLoader,
) {
  const refresh = useCallback(() => {
    const snapshot = stateRef.current.snapshot;
    const revision = snapshot?.historical
      ? snapshot.revision ?? undefined
      : undefined;
    return loadSnapshot(revision);
  }, [loadSnapshot, stateRef]);
  const openRevision = useCallback(
    (revision?: number) => loadSnapshot(revision),
    [loadSnapshot],
  );
  return { refresh, openRevision };
}

export function useCockpitTimelineActions({
  stateRef,
  dispatch,
  loadTimeline,
  requests,
}: {
  stateRef: MutableRefObject<CockpitControllerState>;
  dispatch: Dispatch<CockpitControllerAction>;
  loadTimeline: TimelineLoader;
  requests: CockpitRequestCoordinator;
}) {
  const setTimelineQuery = useCallback(
    (query: ManagementTimelineQuery) => {
      const firstPage = { ...query, page: 1 };
      dispatch({ type: 'timelineQueryChanged', query: firstPage });
      return loadTimeline(firstPage, true);
    },
    [dispatch, loadTimeline],
  );
  const loadMoreTimeline = useCallback(() => {
    const { page, query } = stateRef.current.timeline;
    if (!page?.hasMore || requests.hasActive()) return Promise.resolve();
    const nextPage = { ...query, page: page.page + 1 };
    dispatch({ type: 'timelineQueryChanged', query: nextPage });
    return loadTimeline(nextPage, false);
  }, [dispatch, loadTimeline, requests, stateRef]);
  return { setTimelineQuery, loadMoreTimeline };
}

export function useCockpitSelectionActions(
  stateRef: MutableRefObject<CockpitControllerState>,
  dispatch: Dispatch<CockpitControllerAction>,
) {
  const select = useCallback(
    (kind: CockpitNodeKind, id: string) =>
      dispatch({
        type: 'select',
        selection: {
          kind,
          id,
          revision: stateRef.current.model?.revision ?? null,
        },
      }),
    [dispatch, stateRef],
  );
  const clear = useCallback(
    () => dispatch({ type: 'clearSelection' }),
    [dispatch],
  );
  return { select, clear };
}
